import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, FileText, FileSpreadsheet, Filter as FilterIcon, BarChart3, GitCompareArrows, X as XIcon, BookOpen, Lock } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar,
} from 'recharts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import PostEventInsights from '../components/reports/PostEventInsights';
import { MultiSelectFilter } from '../components/reports/MultiSelectFilter';
import useFeatureGating from '../hooks/useFeatureGating';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'pending_approval', label: 'Pending' },
  { value: 'access_granted',   label: 'Granted'  },
  { value: 'used',             label: 'Used'     },
  { value: 'revoked',          label: 'Revoked'  },
];

const STATUS_COLORS = {
  pending_approval: '#FFB800',
  access_granted:   '#C6A66B',
  used:             '#6EE7B7',
  revoked:          '#C44545',
};

function qsFromFilters(f) {
  const p = new URLSearchParams();
  if (f.from) p.set('from', f.from);
  if (f.to) p.set('to', f.to);
  if (f.event_ids.length) p.set('event_ids', f.event_ids.join(','));
  if (f.statuses.length) p.set('status', f.statuses.join(','));
  if (f.tiers.length) p.set('tier', f.tiers.join(','));
  if (f.payment_methods.length) p.set('payment_method', f.payment_methods.join(','));
  if ((f.add_ons || []).length) p.set('add_on', f.add_ons.join(','));
  if (f.comp_mode && f.comp_mode !== 'all') p.set('comp', f.comp_mode);
  if (f.checked_in_mode && f.checked_in_mode !== 'all') p.set('checked_in', f.checked_in_mode);
  return p.toString();
}

const COMP_OPTIONS = [
  { value: 'all',  label: 'All' },
  { value: 'paid', label: 'Paid' },
  { value: 'comp', label: 'Comp' },
];
const CHECKED_IN_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'Checked In' },
  { value: 'no',  label: 'Not Checked In' },
];

export default function ReportsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    from: '', to: '', event_ids: [], statuses: [],
    tiers: [], payment_methods: [], add_ons: [], comp_mode: 'all', checked_in_mode: 'all',
  });
  const [filtersOpen, setFiltersOpen] = useState(false); // mobile drawer
  const [report, setReport] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(null); // 'csv' | 'pdf' | 'xlsx' | null
  const [compareOpen, setCompareOpen] = useState(false);

  // Keep a stable list of tier/payment options from the FIRST unfiltered load so
  // applying a tier/pm filter doesn't shrink the pill list (which would strand
  // the selected filter with no way to toggle it off).
  const [availableTiers, setAvailableTiers] = useState([]);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);
  const [availableAddOns, setAvailableAddOns] = useState([]);

  // Feature gating — soft indicator only (functional filters still work).
  const { canUse } = useFeatureGating();
  const canUseAdvanced = canUse('advanced_reports');
  const canUseGuestAttendance = canUse('guest_attendance');
  const canUseExpandedExports = canUse('expanded_exports');

  // Load event list (once) for the event multi-select
  useEffect(() => {
    const token = localStorage.getItem('session_token');
    axios.get(`${API}/events`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setEvents(r.data || []))
      .catch(() => setEvents([]));
  }, []);

  const fetchReport = useCallback(async (f) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/reports/summary?${qsFromFilters(f)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(res.data);
      // Seed option lists from the first load that has no tier/pm filters applied,
      // so the pill lists remain stable even when filters narrow the result set.
      const noTierFilter = !(f.tiers || []).length;
      const noPmFilter = !(f.payment_methods || []).length;
      if (noTierFilter) {
        const tiers = (res.data?.ticket_type_breakdown || []).map(r => r.tier_name).filter(Boolean);
        setAvailableTiers(prev => (prev.length === 0 ? Array.from(new Set(tiers)) : prev));
      }
      if (noPmFilter) {
        const pms = (res.data?.payment_method_mix || []).map(r => r.method).filter(Boolean);
        setAvailablePaymentMethods(prev => (prev.length === 0 ? Array.from(new Set(pms)) : prev));
      }
      const noAddOnFilter = !(f.add_ons || []).length;
      if (noAddOnFilter) {
        const aos = (res.data?.add_on_breakdown || []).map(r => r.add_on_name).filter(Boolean);
        setAvailableAddOns(prev => (prev.length === 0 ? Array.from(new Set(aos)) : prev));
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReport(filters); }, [fetchReport, filters]);

  const handleFilterChange = (patch) => setFilters(prev => ({ ...prev, ...patch }));
  const toggleInList = (key, value) => setFilters(prev => ({
    ...prev,
    [key]: prev[key].includes(value) ? prev[key].filter(v => v !== value) : [...prev[key], value],
  }));
  const resetFilters = () => setFilters({
    from: '', to: '', event_ids: [], statuses: [],
    tiers: [], payment_methods: [], add_ons: [], comp_mode: 'all', checked_in_mode: 'all',
  });

  const doExport = async (kind) => {
    setExporting(kind);
    try {
      const token = localStorage.getItem('session_token');
      const url = `${API}/reports/export.${kind}?${qsFromFilters(filters)}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      const cd = res.headers['content-disposition'] || '';
      const match = /filename="?([^";]+)"?/i.exec(cd);
      a.download = match ? match[1] : `velvetgate-report.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);
      toast.success(`${kind.toUpperCase()} export ready`);
    } catch {
      toast.error(`${kind.toUpperCase()} export failed`);
    } finally {
      setExporting(null);
    }
  };

  const kpis = report?.kpis || { total_passes: 0, total_attendees: 0, checked_in_percent: 0, revoked_count: 0 };
  const series = useMemo(() => (report?.tickets_over_time || []).map(r => ({ ...r, date: r.date.slice(5) })), [report]);
  const statusRows = useMemo(() => (report?.status_breakdown || []).map(r => ({
    ...r, event_name: r.event_name.length > 22 ? r.event_name.slice(0, 20) + '…' : r.event_name,
  })), [report]);

  // Compare view: use existing event rows filtered to the selected event_ids
  const compareRows = useMemo(() => {
    if (!report?.events || filters.event_ids.length < 2) return [];
    const picked = new Set(filters.event_ids);
    return report.events.filter(r => picked.has(r.event_id));
  }, [report, filters.event_ids]);

  const goToEventDetail = (eventId) => {
    const qs = new URLSearchParams();
    if (filters.from) qs.set('from', filters.from);
    if (filters.to) qs.set('to', filters.to);
    if (filters.statuses.length) qs.set('status', filters.statuses.join(','));
    const suffix = qs.toString() ? `?${qs}` : '';
    navigate(`/reports/event/${eventId}${suffix}`);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#EAE7E1]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-[#AFAFAF] hover:text-[#D4AF37] h-9 px-2"
              data-testid="reports-back"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <BarChart3 className="w-5 h-5 text-[#D4AF37]" />
            <h1 className="text-xl font-semibold tracking-tight">Reports</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#D4AF37] rounded-sm"
              onClick={() => doExport('csv')}
              disabled={exporting !== null || loading}
              data-testid="reports-export-csv"
            >
              <Download className="w-4 h-4 mr-2" />{exporting === 'csv' ? 'Exporting…' : 'CSV'}
            </Button>
            <Button
              variant="outline"
              className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#D4AF37] rounded-sm"
              onClick={() => doExport('pdf')}
              disabled={exporting !== null || loading}
              data-testid="reports-export-pdf"
            >
              <FileText className="w-4 h-4 mr-2" />{exporting === 'pdf' ? 'Exporting…' : 'PDF'}
            </Button>
            <Button
              variant="outline"
              className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#D4AF37] rounded-sm relative"
              onClick={() => doExport('xlsx')}
              disabled={exporting !== null || loading}
              data-testid="reports-export-xlsx"
              title={canUseExpandedExports ? undefined : 'Pro feature — expanded sheets'}
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />{exporting === 'xlsx' ? 'Exporting…' : 'XLSX'}
              {!canUseExpandedExports && (
                <span className="ml-2 text-[9px] uppercase tracking-widest text-[#D4AF37]/80" data-testid="reports-xlsx-pro-label">Pro</span>
              )}
            </Button>
            <Button
              variant="outline"
              className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#D4AF37] rounded-sm lg:hidden"
              onClick={() => setFiltersOpen(v => !v)}
              data-testid="reports-toggle-filters"
            >
              <FilterIcon className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#D4AF37] rounded-sm"
              onClick={() => navigate('/guide#after-event')}
              data-testid="guide-nav-button"
            >
              <BookOpen className="w-4 h-4 mr-2" /><span className="hidden sm:inline">Guide</span>
            </Button>
          </div>
        </div>

        {/* Filter bar */}
        <div className={`bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4 mb-6 ${filtersOpen ? 'block' : 'hidden'} lg:block`} data-testid="reports-filters">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#777]">From</Label>
              <Input
                type="date" value={filters.from}
                onChange={(e) => handleFilterChange({ from: e.target.value })}
                className="bg-[#050505] border-[#1A1A1A] text-[#EAE7E1] rounded-sm mt-1"
                data-testid="reports-filter-from"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#777]">To</Label>
              <Input
                type="date" value={filters.to}
                onChange={(e) => handleFilterChange({ to: e.target.value })}
                className="bg-[#050505] border-[#1A1A1A] text-[#EAE7E1] rounded-sm mt-1"
                data-testid="reports-filter-to"
              />
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-[11px] uppercase tracking-wider text-[#777]">Events</Label>
            <div className="mt-2">
              <MultiSelectFilter
                label="events"
                options={events.map(e => ({ value: e.event_id, label: e.title }))}
                selected={filters.event_ids}
                onToggle={(v) => toggleInList('event_ids', v)}
                emptyLabel="No events"
                testId="reports-filter-events"
              />
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-[11px] uppercase tracking-wider text-[#777]">Status</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {STATUS_OPTIONS.map(s => {
                const on = filters.statuses.includes(s.value);
                return (
                  <button
                    type="button"
                    key={s.value}
                    onClick={() => toggleInList('statuses', s.value)}
                    className={`px-3 py-1 rounded-sm text-xs border transition-colors ${on ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'bg-[#050505] border-[#1A1A1A] text-[#AFAFAF] hover:border-[#333]'}`}
                    data-testid={`reports-filter-status-${s.value}`}
                  >{s.label}</button>
                );
              })}
            </div>
          </div>

          {/* Phase 4A — additional filters */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="reports-advanced-filters">
            {!canUseAdvanced && (
              <div className="md:col-span-3 flex items-center gap-2 text-[11px] text-[#D4AF37] bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-sm px-3 py-2" data-testid="reports-advanced-lock-banner">
                <Lock className="w-3 h-3" />
                <span>Advanced filters are a <b>Pro</b> feature. Still available to preview — upgrade to remove this hint.</span>
              </div>
            )}
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#777] flex items-center gap-1.5">
                Tier {!canUseAdvanced && <Lock className="w-3 h-3 text-[#D4AF37]/60" data-testid="reports-tier-lock" />}
              </Label>
              <div className="mt-2">
                <MultiSelectFilter
                  label="tier"
                  options={availableTiers}
                  selected={filters.tiers}
                  onToggle={(v) => toggleInList('tiers', v)}
                  emptyLabel="No tiers"
                  testId="reports-filter-tiers"
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#777] flex items-center gap-1.5">
                Payment Method {!canUseAdvanced && <Lock className="w-3 h-3 text-[#D4AF37]/60" />}
              </Label>
              <div className="mt-2">
                <MultiSelectFilter
                  label="payment method"
                  options={availablePaymentMethods}
                  selected={filters.payment_methods}
                  onToggle={(v) => toggleInList('payment_methods', v)}
                  emptyLabel="No methods"
                  testId="reports-filter-payment-methods"
                  capitalize
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#777] flex items-center gap-1.5">
                Add-On {!canUseAdvanced && <Lock className="w-3 h-3 text-[#D4AF37]/60" />}
              </Label>
              <div className="mt-2">
                <MultiSelectFilter
                  label="add-on"
                  options={availableAddOns}
                  selected={filters.add_ons}
                  onToggle={(v) => toggleInList('add_ons', v)}
                  emptyLabel="No add-ons"
                  testId="reports-filter-add-ons"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#777] flex items-center gap-1.5">
                Comp / Paid {!canUseAdvanced && <Lock className="w-3 h-3 text-[#D4AF37]/60" />}
              </Label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="reports-filter-comp-group">
                {COMP_OPTIONS.map(o => {
                  const on = (filters.comp_mode || 'all') === o.value;
                  return (
                    <button
                      type="button"
                      key={o.value}
                      onClick={() => handleFilterChange({ comp_mode: o.value })}
                      className={`px-3 py-1 rounded-sm text-xs border transition-colors ${on ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'bg-[#050505] border-[#1A1A1A] text-[#AFAFAF] hover:border-[#333]'}`}
                      data-testid={`reports-filter-comp-${o.value}`}
                    >{o.label}</button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#777] flex items-center gap-1.5">
                Check-In {!canUseAdvanced && <Lock className="w-3 h-3 text-[#D4AF37]/60" />}
              </Label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="reports-filter-checkin-group">
                {CHECKED_IN_OPTIONS.map(o => {
                  const on = (filters.checked_in_mode || 'all') === o.value;
                  return (
                    <button
                      type="button"
                      key={o.value}
                      onClick={() => handleFilterChange({ checked_in_mode: o.value })}
                      className={`px-3 py-1 rounded-sm text-xs border transition-colors ${on ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'bg-[#050505] border-[#1A1A1A] text-[#AFAFAF] hover:border-[#333]'}`}
                      data-testid={`reports-filter-checkin-${o.value}`}
                    >{o.label}</button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-between items-center flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setCompareOpen(v => !v)}
              disabled={filters.event_ids.length < 2}
              className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#D4AF37] rounded-sm h-8 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="reports-compare-toggle"
            >
              <GitCompareArrows className="w-3.5 h-3.5 mr-1.5" />
              {compareOpen ? 'Hide comparison' : `Compare Selected Events${filters.event_ids.length >= 2 ? ` (${filters.event_ids.length})` : ''}`}
            </Button>
            <Button
              variant="ghost"
              onClick={resetFilters}
              className="text-xs text-[#AFAFAF] hover:text-[#D4AF37] h-8"
              data-testid="reports-filter-reset"
            >Reset filters</Button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-[#C44545]/10 border border-[#C44545]/30 rounded-sm p-4 mb-6 text-sm text-[#C44545]" data-testid="reports-error">{error}</div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="reports-kpis">
          <KpiTile label="Total Passes" value={kpis.total_passes} testId="kpi-total-passes" />
          <KpiTile label="Total Attendees" value={kpis.total_attendees} testId="kpi-total-attendees" />
          <KpiTile label="Checked-In %" value={`${kpis.checked_in_percent}%`} testId="kpi-checkin-pct" />
          <KpiTile label="Revoked" value={kpis.revoked_count} testId="kpi-revoked" accent="#C44545" />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4" data-testid="reports-chart-timeseries">
            <h3 className="text-sm font-semibold text-[#EAE7E1] mb-3">Tickets over time</h3>
            <div className="h-60">
              {series.length === 0 ? (
                <p className="text-xs text-[#555] text-center pt-16">No tickets in range</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                    <XAxis dataKey="date" stroke="#777" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#777" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: '#0A0A0A', border: '1px solid #1A1A1A', color: '#EAE7E1' }} />
                    <Line type="monotone" dataKey="count" stroke="#D4AF37" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4" data-testid="reports-chart-status">
            <h3 className="text-sm font-semibold text-[#EAE7E1] mb-3">Status by event</h3>
            <div className="h-60">
              {statusRows.length === 0 ? (
                <p className="text-xs text-[#555] text-center pt-16">No events in range</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statusRows} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                    <XAxis dataKey="event_name" stroke="#777" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
                    <YAxis stroke="#777" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: '#0A0A0A', border: '1px solid #1A1A1A', color: '#EAE7E1' }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="pending_approval" stackId="a" fill={STATUS_COLORS.pending_approval} name="Pending" />
                    <Bar dataKey="access_granted"   stackId="a" fill={STATUS_COLORS.access_granted}   name="Granted" />
                    <Bar dataKey="used"             stackId="a" fill={STATUS_COLORS.used}             name="Used" />
                    <Bar dataKey="revoked"          stackId="a" fill={STATUS_COLORS.revoked}          name="Revoked" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Sales Snapshot — additive, reuses existing report fields */}
        <SalesSnapshot report={report} loading={loading} />

        {/* Post Event Insights — shown only when exactly one event is selected */}
        <PostEventInsights report={report} filters={filters} />

        {/* Phase 4A — Guest Attendance Summary (additive) */}
        <GuestAttendance report={report} loading={loading} canUse={canUseGuestAttendance} />

        {/* Compare panel */}
        {compareOpen && filters.event_ids.length >= 2 && (
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-sm p-4 mb-6" data-testid="reports-compare-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[#EAE7E1] flex items-center gap-2">
                <GitCompareArrows className="w-4 h-4 text-[#D4AF37]" />
                Side-by-side comparison
              </h3>
              <button
                onClick={() => setCompareOpen(false)}
                className="text-[#555] hover:text-[#EAE7E1] transition-colors"
                data-testid="reports-compare-close"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
            {compareRows.length === 0 ? (
              <p className="text-xs text-[#555] text-center py-8">No matching events in the current date/status window</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm" data-testid="reports-compare-table">
                  <thead className="border-b border-[#1A1A1A]">
                    <tr className="text-left">
                      <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-[#777] font-semibold">Metric</th>
                      {compareRows.map(r => (
                        <th key={r.event_id} className="px-3 py-2 text-[10px] uppercase tracking-wider text-[#D4AF37] font-semibold text-right max-w-[180px] truncate" title={r.event_name}>
                          {r.event_name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Total Passes', r => r.total_passes, '#EAE7E1'],
                      ['Total Attendees', r => r.total_attendees, '#EAE7E1'],
                      ['Checked-In', r => r.checked_in_count, '#C6A66B'],
                      ['Checked-In %', r => (r.total_passes ? `${Math.round(100 * r.checked_in_count / r.total_passes)}%` : '0%'), '#C6A66B'],
                      ['Revoked', r => r.revoked_count, '#C44545'],
                    ].map(([label, getter, color]) => (
                      <tr key={label} className="border-b border-[#111]">
                        <td className="px-3 py-2 text-[#AFAFAF] font-medium">{label}</td>
                        {compareRows.map(r => (
                          <td key={r.event_id} className="px-3 py-2 text-right" style={{ color }}>
                            {getter(r)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Table */}
        <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm overflow-x-auto" data-testid="reports-table">
          <table className="min-w-full text-sm">
            <thead className="border-b border-[#1A1A1A]">
              <tr className="text-left">
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-[#777] font-semibold">Event</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-[#777] font-semibold text-right">Passes</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-[#777] font-semibold text-right">Attendees</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-[#777] font-semibold text-right">Checked-In</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-[#777] font-semibold text-right">Revoked</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-xs text-[#555]" data-testid="reports-loading">Loading…</td></tr>
              )}
              {!loading && (report?.events || []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-xs text-[#555]" data-testid="reports-empty">No events in range</td></tr>
              )}
              {!loading && (report?.events || []).map(r => (
                <tr
                  key={r.event_id}
                  className="border-b border-[#111] hover:bg-[#0D0D0D] cursor-pointer transition-colors"
                  onClick={() => goToEventDetail(r.event_id)}
                  data-testid={`reports-row-${r.event_id}`}
                >
                  <td className="px-4 py-3 text-[#EAE7E1]">{r.event_name}</td>
                  <td className="px-4 py-3 text-right text-[#EAE7E1]">{r.total_passes}</td>
                  <td className="px-4 py-3 text-right text-[#EAE7E1]">{r.total_attendees}</td>
                  <td className="px-4 py-3 text-right text-[#C6A66B]">{r.checked_in_count}</td>
                  <td className="px-4 py-3 text-right text-[#C44545]">{r.revoked_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-[10px] text-[#555] mt-4 text-center">
          Payments reported off-platform. Counts derived from VelvetGate ticket records only.
        </p>
      </div>
    </div>
  );
}

function KpiTile({ label, value, testId, accent }) {
  return (
    <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: accent || '#D4AF37' }}>{value}</div>
    </div>
  );
}

function formatPaymentMethod(method) {
  if (!method) return 'Other';
  const m = String(method).toLowerCase().trim();
  if (m.includes('venmo')) return 'Venmo';
  if (m.includes('zelle')) return 'Zelle';
  if (m.includes('paypal')) return 'PayPal';
  if (m.includes('stripe') || m.includes('card')) return 'Card';
  return 'Other';
}

const PAYMENT_LABEL_ORDER = ['Venmo', 'Zelle', 'PayPal', 'Card', 'Other'];

function groupPaymentMethods(rows) {
  // Collapse raw methods → display labels, summing counts + percents (no data mutation).
  const byLabel = new Map();
  for (const r of rows) {
    const label = formatPaymentMethod(r.method);
    const prev = byLabel.get(label) || { method: label, count: 0, percent: 0 };
    prev.count += Number(r.count) || 0;
    prev.percent += Number(r.percent) || 0;
    byLabel.set(label, prev);
  }
  return Array.from(byLabel.values())
    .map(r => ({ ...r, percent: Math.round(r.percent * 10) / 10 }))
    .sort((a, b) => PAYMENT_LABEL_ORDER.indexOf(a.method) - PAYMENT_LABEL_ORDER.indexOf(b.method));
}

function SalesSnapshot({ report, loading }) {
  if (loading && !report) return null;
  const tiers = report?.ticket_type_breakdown || [];
  const cvp = report?.comp_vs_paid || { paid_count: 0, comp_count: 0, comp_percent: 0 };
  const pm = groupPaymentMethods(report?.payment_method_mix || []);
  const gross = Number(report?.reported_gross_total || 0);
  const grossLabel = gross.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
  // Phase 2H — refund-aware revenue
  const refundedGross = Number(report?.refunded_gross || 0);
  const refundedCount = Number(report?.refunded_count || 0);
  const netGross = Number(report?.net_gross || 0);
  const refundedLabel = refundedGross.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
  const netLabel = netGross.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
  // Phase 2I — Admin-only Platform Fee Earned
  const isAdmin = (report?.filters?.scope || '') === 'admin';
  const platformFee = Number(report?.platform_fee_earned || 0);
  const platformFeeTxn = Number(report?.platform_fee_transaction_count || 0);
  const platformFeeLabel = platformFee.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
  // Phase 2J — Admin-only effective take rate (subtext on the same card)
  const takeRate = Number(report?.platform_take_rate || 0);

  return (
    <section className="mb-6" data-testid="sales-snapshot">
      <h2 className="text-sm font-semibold text-[#EAE7E1] mb-3 tracking-wide uppercase">Sales Snapshot</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Ticket Type Breakdown */}
        <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4 lg:col-span-2" data-testid="snapshot-tier-card">
          <h3 className="text-xs font-semibold text-[#C6A66B] uppercase tracking-wider mb-3">Ticket Type</h3>
          {tiers.length === 0 ? (
            <p className="text-xs text-[#555] py-6 text-center">No tickets in range</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="snapshot-tier-table">
                <thead className="border-b border-[#1A1A1A]">
                  <tr className="text-left">
                    <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#777] font-semibold">Tier</th>
                    <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#777] font-semibold text-right">Tickets</th>
                    <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#777] font-semibold text-right">Attendees</th>
                    <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#777] font-semibold text-right">%</th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((r, i) => (
                    <tr key={`${r.tier_name}-${i}`} className="border-b border-[#111] last:border-0" data-testid={`snapshot-tier-row-${i}`}>
                      <td className="px-2 py-2 text-[#EAE7E1]">{r.tier_name}</td>
                      <td className="px-2 py-2 text-right text-[#EAE7E1]">{r.tickets_sold}</td>
                      <td className="px-2 py-2 text-right text-[#AFAFAF]">{r.attendees}</td>
                      <td className="px-2 py-2 text-right text-[#C6A66B]">{r.percent_of_total}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Comp vs Paid + Gross */}
        <div className="space-y-4">
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4" data-testid="snapshot-compvspaid-card">
            <h3 className="text-xs font-semibold text-[#C6A66B] uppercase tracking-wider mb-3">Comp vs Paid</h3>
            <div className="grid grid-cols-3 gap-2">
              <MiniStat label="Paid" value={cvp.paid_count} testId="snapshot-paid" />
              <MiniStat label="Comp" value={cvp.comp_count} testId="snapshot-comp" />
              <MiniStat label="% Comp" value={`${cvp.comp_percent}%`} accent="#C6A66B" testId="snapshot-comp-pct" />
            </div>
          </div>

          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4" data-testid="snapshot-gross-card">
            <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">Reported Gross (host-entered)</div>
            <div className="text-2xl font-bold mt-1 text-[#D4AF37]" data-testid="snapshot-gross-value">{grossLabel}</div>
            <div className="text-[10px] text-[#555] mt-1">Sum of ticket amount_paid across filtered tickets.</div>
          </div>

          {/* Phase 2H — Net Revenue */}
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4" data-testid="snapshot-net-gross-card">
            <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">Net Revenue</div>
            <div className="text-2xl font-bold mt-1 text-[#6EE7B7]" data-testid="snapshot-net-gross-value">{netLabel}</div>
            <div className="text-[10px] text-[#555] mt-1">Reported Gross minus refunded amounts.</div>
          </div>

          {/* Phase 2H — Refunded */}
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4" data-testid="snapshot-refunded-card">
            <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">Refunded</div>
            <div className="text-2xl font-bold mt-1 text-[#C44545]" data-testid="snapshot-refunded-value">{refundedLabel}</div>
            <div className="text-[10px] text-[#777] mt-1" data-testid="snapshot-refunded-count">{refundedCount} refund{refundedCount === 1 ? '' : 's'}</div>
            <div className="text-[10px] text-[#555] mt-1" data-testid="snapshot-refunded-note">Refunded tickets are excluded from Net Revenue.</div>
          </div>

          {/* Phase 2I — Platform Fee Earned (admin-only) */}
          {isAdmin && (
            <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4" data-testid="snapshot-platform-fee-card">
              <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">Platform Fee Earned</div>
              <div className="text-2xl font-bold mt-1 text-[#D4AF37]" data-testid="snapshot-platform-fee-value">{platformFeeLabel}</div>
              <div className="text-[10px] text-[#777] mt-1" data-testid="snapshot-platform-fee-count">{platformFeeTxn} paid transaction{platformFeeTxn === 1 ? '' : 's'}</div>
              <div className="text-[10px] text-[#6EE7B7] mt-1 font-semibold" data-testid="snapshot-platform-take-rate">Take rate: {takeRate.toFixed(2)}%</div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Method Mix */}
      <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4 mt-4" data-testid="snapshot-payment-card">
        <h3 className="text-xs font-semibold text-[#C6A66B] uppercase tracking-wider mb-3">Payment Method Mix <span className="text-[10px] text-[#555] normal-case">· paid tickets only</span></h3>
        {pm.length === 0 ? (
          <p className="text-xs text-[#555] py-4 text-center">No paid tickets in range</p>
        ) : (
          <div className="space-y-2" data-testid="snapshot-payment-list">
            {pm.map((m, i) => (
              <div key={`${m.method}-${i}`} className="flex items-center gap-3" data-testid={`snapshot-payment-row-${m.method}`}>
                <div className="w-24 text-sm text-[#EAE7E1] capitalize truncate" title={m.method}>{m.method}</div>
                <div className="flex-1 h-2 bg-[#111] rounded-sm overflow-hidden">
                  <div className="h-full bg-[#C6A66B]" style={{ width: `${m.percent}%` }} />
                </div>
                <div className="w-16 text-right text-xs text-[#C6A66B]">{m.percent}%</div>
                <div className="w-12 text-right text-xs text-[#777]">{m.count}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Phase 4B — Add-On Sales */}
      <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4 mt-4" data-testid="snapshot-addons-card">
        <h3 className="text-xs font-semibold text-[#C6A66B] uppercase tracking-wider mb-3">Add-On Sales</h3>
        {(report?.add_on_breakdown || []).length === 0 ? (
          <p className="text-xs text-[#555] py-4 text-center" data-testid="snapshot-addons-empty">No add-ons in range</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="snapshot-addons-table">
              <thead className="border-b border-[#1A1A1A]">
                <tr className="text-left">
                  <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#777] font-semibold">Add-On</th>
                  <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#777] font-semibold text-right">Qty</th>
                  <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#777] font-semibold text-right">Tickets</th>
                  <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#777] font-semibold text-right">Reported Gross</th>
                  <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#777] font-semibold text-right">% of AO</th>
                </tr>
              </thead>
              <tbody>
                {(report?.add_on_breakdown || []).map((row, i) => (
                  <tr key={`${row.add_on_name}-${i}`} className="border-b border-[#111] last:border-0" data-testid={`snapshot-addons-row-${i}`}>
                    <td className="px-2 py-2 text-[#EAE7E1]">{row.add_on_name}</td>
                    <td className="px-2 py-2 text-right text-[#EAE7E1]">{row.quantity_sold}</td>
                    <td className="px-2 py-2 text-right text-[#AFAFAF]">{row.tickets_count}</td>
                    <td className="px-2 py-2 text-right text-[#D4AF37]">${(row.reported_gross_total || 0).toFixed(2)}</td>
                    <td className="px-2 py-2 text-right text-[#C6A66B]">{row.percent_of_add_on_sales}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function MiniStat({ label, value, testId, accent }) {
  return (
    <div className="bg-[#050505] border border-[#111] rounded-sm p-3 text-center" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">{label}</div>
      <div className="text-lg font-bold mt-1" style={{ color: accent || '#EAE7E1' }}>{value}</div>
    </div>
  );
}

function formatLastSeen(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

function GuestAttendance({ report, loading, canUse }) {
  if (loading && !report) return null;
  const rows = report?.guest_attendance_summary || [];
  return (
    <section className="mb-6" data-testid="guest-attendance">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold text-[#EAE7E1] tracking-wide uppercase">Guest Attendance</h2>
        {canUse === false && (
          <span className="text-[9px] uppercase tracking-widest text-[#D4AF37]/80 border border-[#D4AF37]/25 rounded-sm px-1.5 py-0.5" data-testid="guest-attendance-pro-label">Pro feature</span>
        )}
      </div>
      <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4" data-testid="guest-attendance-card">
        {rows.length === 0 ? (
          <p className="text-xs text-[#555] py-6 text-center" data-testid="guest-attendance-empty">No guests in range</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="guest-attendance-table">
              <thead className="border-b border-[#1A1A1A]">
                <tr className="text-left">
                  <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#777] font-semibold">Guest</th>
                  <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#777] font-semibold text-right">Events</th>
                  <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#777] font-semibold text-right">Passes</th>
                  <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#777] font-semibold text-right">Attendees</th>
                  <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#777] font-semibold text-right">Checked-In</th>
                  <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#777] font-semibold text-right whitespace-nowrap">Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.guest_email}-${i}`} className="border-b border-[#111] last:border-0" data-testid={`guest-attendance-row-${r.guest_email}`}>
                    <td className="px-2 py-2 min-w-0">
                      <div className="text-[#EAE7E1] truncate max-w-[220px]" title={r.guest_name || r.guest_email}>
                        {r.guest_name || <span className="text-[#777] italic">no name</span>}
                      </div>
                      <div className="text-[11px] text-[#777] truncate max-w-[220px]" title={r.guest_email}>{r.guest_email}</div>
                    </td>
                    <td className="px-2 py-2 text-right text-[#EAE7E1]">{r.events_attended}</td>
                    <td className="px-2 py-2 text-right text-[#EAE7E1]">{r.total_passes}</td>
                    <td className="px-2 py-2 text-right text-[#AFAFAF]">{r.total_attendees}</td>
                    <td className="px-2 py-2 text-right text-[#C6A66B]">{r.checked_in_count}</td>
                    <td className="px-2 py-2 text-right text-[#777] whitespace-nowrap">{formatLastSeen(r.last_attended_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
